import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Person as PersonIcon,
  Help as HelpIcon,
  Home as HomeIcon,
  Description as DescriptionIcon,
  Filter1 as Filter1Icon,
  Filter2 as Filter2Icon,
  Filter3 as Filter3Icon,
  ManageAccounts as ManageAccountsIcon,
  BarChart as BarChartIcon,
} from "@mui/icons-material";
import { useMsal } from "@azure/msal-react";
import { colors } from "../theme/colors";
import Q8Button from "../components/Q8Button";
import Homepage from "../pages/Homepage";
import AfeTable from "../pages/AfeTable";
import Level1Table from "../pages/Level1Table";
import Level2Table from "../pages/Level2Table";
import Level3Table from "../pages/Level3Table";
import OpexTable from "../pages/OpexTable";
import Level1CapexTable from "../pages/Level1CapexTable";
import Level2CapexTable from "../pages/Level2CapexTable";
import Level3CapexTable from "../pages/Level3CapexTable";
import UserManagement from "../pages/UserManagement";
import ReportLocal from "../pages/ReportLocal"; // Import new report pages
import ReportCorporate from "../pages/ReportCorporate"; // Import new report pages
import LogAdmin from "../pages/LogAdmin";
import { removeAuthToken } from "../utils/cookieUtils.js";
import Loader from "../components/Loader/Loader.js";
import { LoaderContext } from "../context/LoaderContext.js";
import configureHttpInterceptor from "../interceptors/htttpInterceptor.js";
import UserService from "../service/userService.js";
import { UserContext } from "../context/UserContext.js";
import AfePendingChanges from "../pages/AfePendingChanges";
import AfeChangeHistory from "../pages/AfeChangeHistory";
import OpexPendingChanges from "../pages/OpexPendingChanges";
import OpexChangeHistory from "../pages/OpexChangeHistory";
import AccountsConfiguration from "../pages/AccountsConfiguration";
import CDCConfiguration from "../pages/CDCConfiguration.js";
import ExpenseItemsConfiguration from "../pages/ExpenseItemsConfiguration.js";
import LogoutIcon from "@mui/icons-material/Logout";
import Documentation from "../pages/Documentation.js";
import AccountsConfigurationReadOnly from "../pages/AccountsConfigurationReadOnly.js";
import CDCConfigurationReadOnly from "../pages/CDCConfigurationReadOnly.js";

function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { accounts, instance } = useMsal();
  const drawerWidth = 230;
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [userRoles, setUserRoles] = useState([]);
  const { setUser } = useContext(UserContext);
  const { isLoading, setIsLoading } = useContext(LoaderContext);

  // Funzione per pulire la cache MSAL
  const clearMsalCache = () => {
    // Rimuovi tutti gli elementi MSAL da sessionStorage
    Object.keys(sessionStorage).forEach((key) => {
      if (
        key.startsWith("msal.") ||
        key.includes("login") ||
        key.includes("redirect")
      ) {
        sessionStorage.removeItem(key);
      }
    });

    // Rimuovi tutti gli elementi MSAL da localStorage
    Object.keys(localStorage).forEach((key) => {
      if (
        key.startsWith("msal.") ||
        key.includes("login") ||
        key.includes("redirect")
      ) {
        localStorage.removeItem(key);
      }
    });

    // Rimuovi tutti i cookie correlati a MSAL
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

      if (
        name.startsWith("msal.") ||
        name.includes("auth") ||
        name.includes("login")
      ) {
        document.cookie =
          name + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      }
    }

    // Rimuovi le variabili di stato della sessione
    sessionStorage.removeItem("redirectComplete");
    sessionStorage.removeItem("loginRedirect");
  };

  // Funzione migliorata per il logout
  const handleLogout = async () => {
    try {
      // Attiva il loader durante il logout
      setIsLoading(true);

      // 1. Pulisci la cache MSAL
      clearMsalCache();

      // 2. Rimuovi il token di autenticazione dell'app
      removeAuthToken();

      // 3. Imposta l'account attivo a null prima del logout
      if (instance.getActiveAccount()) {
        instance.setActiveAccount(null);
      }

      // 4. Esegui il logout da MSAL
      const logoutRequest = {
        account: null,
        postLogoutRedirectUri: window.location.origin,
        onRedirectNavigate: (url) => {
          // Impediamo la navigazione automatica per gestire il flusso manualmente
          return false;
        },
      };

      // Usa logoutPopup per evitare reindirizzamenti problematici
      // await instance.logoutPopup(logoutRequest);

      // 5. Pulisci nuovamente la cache dopo il logout
      clearMsalCache();

      // 6. Reindirizza alla pagina di login
      setTimeout(() => {
        navigate("/login");
      }, 500);
    } catch (error) {
      // In caso di errore, pulisci comunque tutto e forza un reload
      clearMsalCache();
      removeAuthToken();
      window.location.href = `${window.location.origin}/login`;
    } finally {
      setIsLoading(false);
    }
  };

  const isAdmin = userRoles?.some((role) => role.roleName === "Admin");
  const isAdminOrValidator = userRoles?.some((role) =>
    ["Admin", "Validator"].includes(role.roleName)
  );

  const baseMenuItems = [
    { text: "Home", icon: <HomeIcon />, path: "/" },
    { text: "OPEX", icon: <DescriptionIcon />, path: "/opex" },
    { text: "OPEX Allocation", icon: <DescriptionIcon />, path: "/opex-allocation" },
    { text: "CAPEX", icon: <DescriptionIcon />, path: "/capex" },
    { text: "Report Local", icon: <BarChartIcon />, path: "/report-local" },
    {
      text: "Report Corporate",
      icon: <BarChartIcon />,
      path: "/report-corporate",
    },
  ];

  // Configuration menu items
  const configMenuItems = [
    {
      text: "Config Conti",
      icon: <Filter1Icon />,
      path: "/accounts-configuration",
    },
    {
      text: "Config CDC",
      icon: <Filter2Icon />,
      path: "/cdc-configuration",
    },
    {
      text: "Config Voci Opex",
      icon: <Filter3Icon />,
      path: "/expense-items-configuration",
    },
  ];

  // CAPEX levels menu items
  const capexLevelMenuItems = [
    { text: "Config Liv 1 CAPEX", icon: <Filter1Icon />, path: "/level1Capex" },
    { text: "Config Liv 2 CAPEX", icon: <Filter2Icon />, path: "/level2Capex" },
    { text: "Config Liv 3 CAPEX", icon: <Filter3Icon />, path: "/level3Capex" },
  ];

  // Report menu items for Admin and Validator roles
  // const reportMenuItems = [
  //   {
  //     text: "Report Local",
  //     icon: <BarChartIcon />,
  //     path: "/report-local",
  //   },
  //   {
  //     text: "Report Corporate",
  //     icon: <BarChartIcon />,
  //     path: "/report-corporate",
  //   },
  // ];

  // Admin-only menu items
  const adminMenuItems = [
    {
      text: "Gestione Utenti",
      icon: <ManageAccountsIcon />,
      path: "/usermanagement",
    },
    {
      text: "Log Admin",
      icon: <DescriptionIcon />,
      path: "/log-admin",
    },
  ];

  const coordinatorMenuItems = [
    {
      text: "Config Conti Coord",
      icon: <Filter1Icon />,
      path: "/accounts-tech",
    },
    {
      text: "Config CDC Coord",
      icon: <Filter2Icon />,
      path: "/cdc-view",
    },
  ];

  const footerMenuItems = [
    {
      text: "Documentazione",
      icon: <DescriptionIcon />,
      path: "/documentation",
    },
  ];

  useEffect(() => {
    configureHttpInterceptor(setIsLoading);
  }, [setIsLoading]);

  const checkAuthStatus = async (accounts) => {
    if (!accounts.length) {
      setIsAuthenticated(false);
      setIsCheckingAuth(false);
      return;
    }
    try {
      // Verifica se esiste un token nei cookie
      const token = document.cookie
        .split("; ")
        .find((row) => row.startsWith("Q8TechAuth="))
        ?.split("=")[1];

      if (!token) {
        setIsAuthenticated(false);
        setIsCheckingAuth(false);
        return;
      }

      const userData = await UserService.getUserInfo();
      setUser(userData);

      setIsAuthenticated(true);
      setUserRoles(userData.userRoles || []);
    } catch (error) {
      setIsAuthenticated(false);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  useEffect(() => {
    checkAuthStatus(accounts);
  }, [accounts.length]);

  if (isCheckingAuth) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Combine menu items based on user roles
  // Nuovo ordine: base, config, capex levels, reports, admin
  const menuItems = [
    ...baseMenuItems,
    ...(isAdminOrValidator ? [...configMenuItems, ...capexLevelMenuItems] : []),
    ...(userRoles?.some((role) => role.roleName === "Coordinator")
      ? coordinatorMenuItems
      : []),
    ...(isAdmin ? adminMenuItems : []),
    ...footerMenuItems,
  ];

  // Funzione per verificare i ruoli
  const checkRole = (allowedRoles) => {
    if (!allowedRoles || allowedRoles.length === 0) return true;
    return userRoles?.some((userRole) =>
      allowedRoles.includes(userRole.roleName)
    );
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap" }}>
      {isLoading ? <Loader></Loader> : <></>}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: colors.gradients.nfoGestori,
          borderBottom: `3px solid ${colors.warning.main}`,
        }}
      >
        <Toolbar sx={{ height: 64 }}>
          <Box
            className="img-logo"
            component="img"
            sx={{
              height: 80,
              mr: 2,
              objectFit: "contain",
              cursor: "pointer",
            }}
            alt="Q8 Logo"
            src="/Logo2-BKWYCREK-removebg.png"
            onClick={() => navigate("/")}
          />

          <Typography
            className="page-title"
            variant="h5"
            component="div"
            sx={{ flexGrow: 1, color: "white" }}
          >
            Q8 TechExpenses
          </Typography>

          <Q8Button
            variant="tertiary"
            label={accounts[0]?.name || "Login"}
            icon={<PersonIcon />}
            sx={{ pointerEvents: "none" }}
          />
          <Q8Button
            variant="tertiary"
            label="Esci"
            icon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          />
        </Toolbar>
      </AppBar>
      <div style={{height: "65px", width: "100%", display: "flex"}}>&nbsp;</div>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
            bgcolor: colors.primary.main,
            color: "white",
            borderRight: "none",
            top: "65px",
            height: "calc(100vh - 65px)"
          },
        }}
      > 
        <Box
          sx={{
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <List>
            {menuItems.map((item) => (
              <ListItem
                key={item.text}
                sx={{
                  px: 1.5,
                  py: 0.75,
                  minHeight: 42,
                  cursor: "pointer",
                  backgroundColor:
                    location.pathname === item.path
                      ? "rgba(255, 255, 255, 0.1)"
                      : "transparent",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
                onClick={() => navigate(item.path)}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: "white",
                    "& svg": {
                      fontSize: 20,
                    },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontSize: "0.9rem",
                      fontWeight:
                        location.pathname === item.path ? "bold" : "normal",
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          backgroundColor: "#f5f5f5",
          // height: "calc(100vh - 65px)",
          width: "calc(100vw - 300px)",
        }}
      >
        <Routes>
          <Route index element={<Homepage />} />
          <Route path="opex" element={<OpexTable onlyAllocation={false} title={"OPEX"} href={"/opex"} pendingHref={"/opex/pending"} historyHref={"/opex/history"} />} />
          <Route path="opex-allocation" element={<OpexTable onlyAllocation={true} title={"OPEX Allocation"} href={"/opex-allocation"} pendingHref={"/opex-allocation/pending"} historyHref={"/opex-allocation/history"} />} />
          <Route path="capex" element={<AfeTable />} />

          {/* CAPEX Protected Routes */}
          <Route
            path="capex/pending"
            element={
              checkRole(["Admin", "Validator"]) ? (
                <AfePendingChanges />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="capex/history"
            element={
              checkRole(["Admin", "Validator"]) ? (
                <AfeChangeHistory />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route path="documentation" element={<Documentation />} />

          {/* OPEX Protected Routes */}
          <Route
            path="opex/pending"
            element={
              checkRole(["Admin", "Validator"]) ? (
                <OpexPendingChanges onlyAllocation={false} title={"OPEX"} href={"/opex"} secondHref={"/opex/pending"}/>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="opex-allocation/pending"
            element={
              checkRole(["Admin", "Validator"]) ? (
                <OpexPendingChanges onlyAllocation={true} title={"OPEX Allocation"} href={"/opex-allocation"} secondHref={"/opex-allocation/pending"}/>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="opex/history"
            element={
              checkRole(["Admin", "Validator"]) ? (
                <OpexChangeHistory onlyAllocation={false} title={"OPEX"} href={"/opex"} secondHref={"/opex/history"} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="opex-allocation/history"
            element={
              checkRole(["Admin", "Validator"]) ? (
                <OpexChangeHistory onlyAllocation={true} title={"OPEX Allocation"} href={"/opex-allocation"} secondHref={"/opex-allocation/history"}/>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          {/* Level Protected Routes */}
          <Route
            path="level1Opex"
            element={
              checkRole(["Admin", "Validator"]) ? (
                <Level1Table />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="level2Opex"
            element={
              checkRole(["Admin", "Validator"]) ? (
                <Level2Table />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="level3Opex"
            element={
              checkRole(["Admin", "Validator"]) ? (
                <Level3Table />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="accounts-configuration"
            element={
              checkRole(["Admin", "Validator"]) ? (
                <AccountsConfiguration />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="cdc-configuration"
            element={
              checkRole(["Admin", "Validator"]) ? (
                <CDCConfiguration />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="expense-items-configuration"
            element={
              checkRole(["Admin", "Validator"]) ? (
                <ExpenseItemsConfiguration />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="level1Capex"
            element={
              checkRole(["Admin", "Validator"]) ? (
                <Level1CapexTable />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="level2Capex"
            element={
              checkRole(["Admin", "Validator"]) ? (
                <Level2CapexTable />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="level3Capex"
            element={
              checkRole(["Admin", "Validator"]) ? (
                <Level3CapexTable />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="accounts-tech"
            element={
              checkRole(["Admin", "Validator", "Coordinator"]) ? (
                <AccountsConfigurationReadOnly />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="cdc-view"
            element={
              checkRole(["Admin", "Validator", "Coordinator"]) ? (
                <CDCConfigurationReadOnly />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          {/* Report Routes - Protected for Admin and Validator */}
          <Route
            path="report-local"
            element={
              checkRole(["Admin", "Validator", "Coordinator"]) ? (
                <ReportLocal />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="report-corporate"
            element={
              checkRole(["Admin", "Validator", "Coordinator"]) ? (
                <ReportCorporate />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          {/* Admin-only route */}
          <Route
            path="userManagement"
            element={
              checkRole(["Admin"]) ? (
                <UserManagement />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="log-admin"
            element={
              checkRole(["Admin"]) ? <LogAdmin /> : <Navigate to="/" replace />
            }
          />
        </Routes>
      </Box>
    </Box>
  );
}

MainLayout.propTypes = {
  userRoles: PropTypes.arrayOf(
    PropTypes.shape({
      roleName: PropTypes.string.isRequired,
    })
  ),
};

export default MainLayout;
